import React, { useState, useEffect } from 'react';
import {
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

// Services
import { bridgeAPI, metadataAPI } from '../services/api';
import SimulationStatusBanner from './SimulationStatusBanner';

const BridgeView = ({ userAddress }) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [targetMetaverse, setTargetMetaverse] = useState('');
  const [bridgeDirection, setBridgeDirection] = useState('to_metaverse'); // 'to_metaverse' or 'from_metaverse'
  const [supportedMetaverses, setSupportedMetaverses] = useState([]);
  const [userAssets, setUserAssets] = useState({ locked: [], wrapped: [] });
  const [bridgeFee, setBridgeFee] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bridgeHistory, setBridgeHistory] = useState([]);

  useEffect(() => {
    loadBridgeData();
  }, [userAddress]);

  useEffect(() => {
    if (selectedAsset && targetMetaverse) {
      calculateBridgeFee();
    }
  }, [selectedAsset, targetMetaverse]);

  const loadBridgeData = async () => {
    try {
      const metaversesResponse = await bridgeAPI.getSupportedMetaverses();
      const metaverseList = metaversesResponse?.data ?? metaversesResponse ?? [];
      setSupportedMetaverses(Array.isArray(metaverseList) ? metaverseList : []);

      if (userAddress) {
        const assetsResponse = await bridgeAPI.getUserAssets(userAddress);
        setUserAssets(assetsResponse?.data ?? assetsResponse ?? { locked: [], wrapped: [] });
      } else {
        setUserAssets({ locked: [], wrapped: [] });
      }

      // Mock bridge history
      setBridgeHistory([
        {
          id: 1,
          asset: 'Galaxia Avatar #123',
          from: 'Galaxia',
          to: 'Ready Player Me',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
          status: 'completed',
          txHash: '0x1234...5678'
        },
        {
          id: 2,
          asset: 'Wrapped Asset #456',
          from: 'OMA3',
          to: 'Galaxia',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48), // 2 days ago
          status: 'completed',
          txHash: '0x5678...9abc'
        }
      ]);
    } catch (error) {
      console.error('Failed to load bridge data:', error);
    }
  };

  const calculateBridgeFee = async () => {
    if (!selectedAsset || !targetMetaverse) return;

    try {
      const assetValue = selectedAsset.value || 100; // Default value if not available
      const response = await bridgeAPI.calculateBridgeFee(targetMetaverse.id, assetValue);
      setBridgeFee(response.data);
    } catch (error) {
      console.error('Failed to calculate bridge fee:', error);
    }
  };

  const handleBridgeAsset = async () => {
    if (!selectedAsset || !targetMetaverse) return;

    setIsProcessing(true);
    try {
      let response;

      if (bridgeDirection === 'to_metaverse') {
        // Bridge from Galaxia to external metaverse
        response = await bridgeAPI.lockAsset({
          userAddress,
          originalContract: selectedAsset.contract,
          tokenId: selectedAsset.tokenId,
          metaverseId: targetMetaverse.id,
          assetType: selectedAsset.type
        });
      } else {
        // Bridge from external metaverse to Galaxia
        response = await bridgeAPI.mintWrappedAsset({
          userAddress,
          originalContract: selectedAsset.contract,
          originalTokenId: selectedAsset.tokenId,
          originalChainId: selectedAsset.chainId,
          metaverseId: targetMetaverse.id,
          signature: 'mock_signature' // Would come from validator
        });
      }

      // Refresh data after successful bridge
      loadBridgeData();

      alert('Asset bridged successfully!');
    } catch (error) {
      console.error('Bridge operation failed:', error);
      alert('Bridge operation failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;

    if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))} minutes ago`;
    if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))} hours ago`;
    return `${Math.floor(diff / (1000 * 60 * 60 * 24))} days ago`;
  };

  return (
    <div className="space-y-6 galaxia-fade-up">
      <SimulationStatusBanner />
      {/* Header */}
      <div className="galaxia-card p-6">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--galaxia-text)' }}>Asset Bridge</h1>
        <p className="mt-1" style={{ color: 'var(--galaxia-muted)' }}>
          Bridge your assets between Galaxia and external metaverses
        </p>
      </div>

      {/* Bridge Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bridge Form */}
        <div className="galaxia-card p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Bridge Asset</h3>

          {/* Direction Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--galaxia-text)' }}>
              Bridge Direction
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setBridgeDirection('to_metaverse')}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  bridgeDirection === 'to_metaverse'
                    ? 'border-[var(--galaxia-accent)]'
                    : ''
                }`}
                style={{
                  background: bridgeDirection === 'to_metaverse' ? 'rgba(206, 147, 216, 0.15)' : 'var(--galaxia-surface)',
                  borderColor: bridgeDirection === 'to_metaverse' ? 'var(--galaxia-accent)' : 'var(--galaxia-border)',
                  color: 'var(--galaxia-text)'
                }}
              >
                To Metaverse
              </button>
              <button
                onClick={() => setBridgeDirection('from_metaverse')}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  bridgeDirection === 'from_metaverse'
                    ? 'border-[var(--galaxia-accent)]'
                    : ''
                }`}
                style={{
                  background: bridgeDirection === 'from_metaverse' ? 'rgba(206, 147, 216, 0.15)' : 'var(--galaxia-surface)',
                  borderColor: bridgeDirection === 'from_metaverse' ? 'var(--galaxia-accent)' : 'var(--galaxia-border)',
                  color: 'var(--galaxia-text)'
                }}
              >
                From Metaverse
              </button>
            </div>
          </div>

          {/* Asset Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--galaxia-text)' }}>
              Select Asset
            </label>
            <select
              onChange={(e) => setSelectedAsset(JSON.parse(e.target.value))}
              className="galaxia-input galaxia-select w-full"
            >
              <option value="">Choose an asset...</option>
              {bridgeDirection === 'to_metaverse' ? (
                userAssets.locked?.map((asset) => (
                  <option key={asset.id} value={JSON.stringify(asset)}>
                    {asset.name} (Locked)
                  </option>
                ))
              ) : (
                userAssets.wrapped?.map((asset) => (
                  <option key={asset.id} value={JSON.stringify(asset)}>
                    {asset.name} (Wrapped)
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Target Metaverse Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--galaxia-text)' }}>
              Target Metaverse
            </label>
            <select
              value={targetMetaverse}
              onChange={(e) => setTargetMetaverse(e.target.value)}
              className="galaxia-input galaxia-select w-full"
            >
              <option value="">Choose target metaverse...</option>
              {supportedMetaverses.map((metaverse) => (
                <option key={metaverse.id} value={metaverse.id}>
                  {metaverse.name}
                </option>
              ))}
            </select>
          </div>

          {/* Bridge Fee Display */}
          {bridgeFee && (
            <div className="mb-6 p-4 rounded-lg" style={{ background: 'var(--galaxia-surface)', border: '1px solid var(--galaxia-border)' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Bridge Fee:</span>
                <span className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                  {bridgeFee.totalFee} {bridgeFee.currency}
                </span>
              </div>
              <div className="mt-2 text-xs" style={{ color: 'var(--galaxia-muted)' }}>
                Base Fee: {bridgeFee.baseFee} {bridgeFee.currency} |
                Dynamic Fee: {bridgeFee.dynamicFee} {bridgeFee.currency}
              </div>
            </div>
          )}

          {/* Bridge Button */}
          <button
            onClick={handleBridgeAsset}
            disabled={!selectedAsset || !targetMetaverse || isProcessing}
            className={`w-full px-4 py-3 text-sm font-medium galaxia-btn-primary ${
              (!selectedAsset || !targetMetaverse || isProcessing) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Processing...
              </div>
            ) : (
              <>
                <ArrowRightIcon className="h-5 w-5 inline mr-2" />
                Bridge Asset
              </>
            )}
          </button>

          {/* Info Box */}
          <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(0, 212, 255, 0.08)', border: '1px solid rgba(0, 212, 255, 0.3)' }}>
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--galaxia-secondary-cta)' }} />
              <div className="ml-3">
                <p className="text-sm" style={{ color: 'var(--galaxia-text)' }}>
                  {bridgeDirection === 'to_metaverse'
                    ? 'Bridging assets to external metaverses locks them in the bridge contract and creates wrapped versions.'
                    : 'Bridging assets from external metaverses creates wrapped versions in Galaxia.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bridge Status & History */}
        <div className="space-y-6">
          <div className="galaxia-card p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Bridge Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Status</span>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full galaxia-status-success">
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Supported Metaverses</span>
                <span className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                  {supportedMetaverses.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--galaxia-muted)' }}>Your Assets</span>
                <span className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                  {userAssets.locked?.length || 0} locked, {userAssets.wrapped?.length || 0} wrapped
                </span>
              </div>
            </div>
          </div>

          <div className="galaxia-card p-6">
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Bridge History</h3>
            <div className="space-y-3">
              {bridgeHistory.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {transaction.status === 'completed' ? (
                        <CheckCircleIcon className="h-5 w-5" style={{ color: 'var(--galaxia-success)' }} />
                      ) : (
                        <ClockIcon className="h-5 w-5" style={{ color: 'var(--galaxia-warning)' }} />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>
                        {transaction.asset}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--galaxia-muted)' }}>
                        {transaction.from} → {transaction.to}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: 'var(--galaxia-muted)' }}>
                      {formatTimeAgo(transaction.timestamp)}
                    </p>
                    <a
                      href={`https://etherscan.io/tx/${transaction.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline"
                      style={{ color: 'var(--galaxia-accent)' }}
                    >
                      View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Supported Metaverses Details */}
      <div className="galaxia-card p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--galaxia-text)' }}>Supported Metaverses</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {supportedMetaverses.map((metaverse) => (
            <div key={metaverse.id} className="rounded-lg p-4" style={{ border: '1px solid var(--galaxia-border)', background: 'rgba(255,255,255,0.03)' }}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium" style={{ color: 'var(--galaxia-text)' }}>{metaverse.name}</h4>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  metaverse.isActive ? 'galaxia-status-success' : 'galaxia-status-error'
                }`}>
                  {metaverse.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--galaxia-muted)' }}>{metaverse.description}</p>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--galaxia-muted)' }}>Bridge Fee:</span>
                  <span className="font-medium" style={{ color: 'var(--galaxia-text)' }}>{metaverse.bridgeFee}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--galaxia-muted)' }}>Max Value:</span>
                  <span className="font-medium" style={{ color: 'var(--galaxia-text)' }}>{metaverse.maxAssetValue}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BridgeView;
